import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Shield, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MFAVerify() {
    const location = useLocation();
    const navigate = useNavigate();
    const { setAuthFromToken } = useAuth();
    
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const inputRefs = useRef([]);

    const email = location.state?.email;
    const tempToken = location.state?.tempToken;

    useEffect(() => {
        if (!email || !tempToken) {
            navigate('/login');
        }
    }, [email, tempToken, navigate]);

    const handleChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        
        const newCode = [...code];
        newCode[index] = value.slice(-1);
        setCode(newCode);

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        if (!/^\d+$/.test(pastedData)) return;
        
        const newCode = [...code];
        for (let i = 0; i < pastedData.length; i++) {
            newCode[i] = pastedData[i];
        }
        setCode(newCode);
        
        if (pastedData.length === 6) {
            inputRefs.current[5]?.focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const fullCode = code.join('');
        
        if (fullCode.length !== 6) {
            toast.error('Please enter the complete 6-digit code');
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.post(`${API}/auth/mfa/verify`, {
                temp_token: tempToken,
                code: fullCode
            });
            
            // Set auth token and user
            setAuthFromToken(response.data.token, response.data.user);
            toast.success('Welcome back!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid code');
            setCode(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        try {
            await axios.post(`${API}/auth/mfa/resend`, { temp_token: tempToken });
            toast.success('New code sent to your email');
        } catch (error) {
            toast.error('Failed to resend code');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-slate-50 flex flex-col">
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                {/* Logo */}
                <div className="flex flex-col items-center mb-6">
                    <img 
                        src="/voilaw-logo.png" 
                        alt="Voilaw Logo" 
                        className="h-32 w-32 object-contain"
                    />
                    <h1 className="font-brand text-3xl text-foreground mt-4">Voilaw</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Time & task tracking for attorneys
                    </p>
                </div>

                <div className="text-center mb-8 max-w-sm">
                    <div className="p-4 bg-blue-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <Shield className="h-8 w-8 text-[#1C6EE7]" />
                    </div>
                    <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                        Two-Factor Authentication
                    </h2>
                    <p className="text-muted-foreground">
                        Enter the 6-digit code sent to <strong>{email}</strong>
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="w-full max-w-sm">
                    {/* 6-digit code input */}
                    <div className="flex justify-center gap-2 mb-6">
                        {code.map((digit, index) => (
                            <Input
                                key={index}
                                ref={(el) => (inputRefs.current[index] = el)}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                className="w-12 h-14 text-center text-2xl font-bold rounded-xl bg-white shadow-soft border-0"
                                data-testid={`mfa-code-input-${index}`}
                            />
                        ))}
                    </div>

                    <div className="pt-4">
                        <Button
                            type="submit"
                            className="w-full sm:w-3/4 mx-auto block h-14 rounded-full text-lg font-semibold bg-[#1C6EE7] hover:bg-[#1559c7] shadow-lg hover:shadow-xl transition-all"
                            disabled={isLoading}
                            data-testid="mfa-verify-btn"
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Clock className="h-5 w-5 animate-spin" />
                                    Verifying...
                                </span>
                            ) : (
                                'Verify Code'
                            )}
                        </Button>
                    </div>
                </form>

                <div className="mt-6 text-center">
                    <button
                        onClick={handleResendCode}
                        className="text-[#1C6EE7] text-sm hover:underline"
                        data-testid="resend-code-btn"
                    >
                        Didn&apos;t receive a code? Resend
                    </button>
                </div>

                <div className="mt-8 text-center">
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to sign in
                    </Link>
                </div>
            </div>
        </div>
    );
}
